import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../../AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ShieldCheck, Sparkles, Building2 } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    try {
      const res = await axios.post(`${API}/api/auth/login`, {
        email,
        password,
      });

      login(res.data.token);

      const params = new URLSearchParams(location.search);

      // redirection exacte vers l'URL d'origine (produit + query params)
      const next = params.get("next");
      if (next) {
        navigate(decodeURIComponent(next), { replace: true });
        return;
      }

      // (fallback) ancien comportement: /login?from=pricing&packType=...&sessions=...
      const from = params.get("from");
      const packType = params.get("packType");
      const sessions = params.get("sessions");

      if (from === "pricing" && packType && sessions) {
        navigate(`/catalogue?packType=${packType}&sessions=${sessions}`, { replace: true });
      } else {
        navigate("/", { replace: true });
      }

    } catch (err) {
      setMessage(err.response?.data?.error || "Erreur de connexion.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden flex items-center justify-center px-4 py-12">
      <div
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(circle at 15% 0%, rgba(99,102,241,0.18) 0, transparent 55%), radial-gradient(circle at 85% 10%, rgba(249,115,22,0.12) 0, transparent 55%)",
        }}
      />

      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(15,23,42,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(15,23,42,0.12) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
          maskImage:
            "radial-gradient(circle at 50% 10%, black 0%, transparent 70%)",
          WebkitMaskImage:
            "radial-gradient(circle at 50% 10%, black 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-5xl w-full grid md:grid-cols-2 gap-10 items-center">
        {/* Colonne gauche */}
        <div className="hidden md:block">
          <p className="text-[11px] tracking-[0.25em] uppercase text-slate-400 mb-3">
            Espace entreprise
          </p>

          <h1 className="text-3xl font-semibold text-slate-900 mb-4 leading-tight">
            Accédez à votre tableau de bord
            <br />
            et gérez vos expériences.
          </h1>

          <p className="text-sm text-slate-600 mb-6 max-w-md">
            Retrouvez vos sessions, vos packs et les factures associées. Vous
             pouvez aussi gérer la répartition des équipes avant chaque partie.
          </p>

          <div className="flex flex-col gap-2 text-[13px] text-slate-600">
            <span className="inline-flex items-center gap-2">
              <Building2 className="h-4 w-4 text-indigo-600" />
              Pensé pour les équipes et managers
            </span>
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-orange-500" />
              Expériences immersives clé-en-main
            </span>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-slate-700" />
              Accès sécurisé & données protégées
            </span>
          </div>
        </div>

        {/* Colonne droite : formulaire */}
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8">
          <h2 className="text-xl font-semibold text-slate-900 text-center mb-1">
            Connexion entreprise
          </h2>
          <p className="text-xs text-slate-500 text-center mb-6">
            Utilisez l’email associé à votre compte.
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Email professionnel
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="prenom.nom@entreprise.com"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Mot de passe
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="••••••••"
              />
            </div>

            {message && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {message}
              </div>
            )}

            <button
              type="submit"
              className="
                w-full mt-2 inline-flex justify-center items-center gap-2
                rounded-xl bg-indigo-600 px-4 py-3
                text-sm font-semibold text-white
                shadow-sm
                hover:bg-indigo-500
                focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-indigo-500
                focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50
                transition
              "
            >
              Se connecter
            </button>
          </form>

          <div className="mt-5 space-y-1 text-center">
            <p className="text-xs text-slate-600">
              Pas encore de compte ?{" "}
              <Link
                to={`/signup${location.search}`}
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Créer un compte
              </Link>
            </p>
            <p className="text-xs text-slate-600">
              Mot de passe oublié ?{" "}
              <Link
                to="/forgot-password"
                className="text-indigo-600 hover:text-indigo-500 font-medium"
              >
                Réinitialiser
              </Link>
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
            Connexion sécurisée – aucune donnée partagée
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
